/**
 * Users administration view.
 *
 * Lists users in the caller's tenant. Administrator can edit role, dial
 * number, capabilities, status inline. OperationsManager has read-only.
 *
 * Per Tom's standing rules:
 *   - Sortable columns by default
 *   - Auto-faceted filters on fields with <10 distinct values (role, status,
 *     pbxEnabled, acdEnabled, roleAssignmentSource)
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, Save, X, Check, Search } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { ASSIGNABLE_ROLES, ROLE_LABELS, type AppRole } from "../lib/roles";
import { formatE164ForDisplay, parseUSPhone } from "../lib/phone";

type User = {
  id: string;
  email: string;
  displayName: string | null;
  role: AppRole;
  roleAssignmentSource: string;
  status: string;
  pbxEnabled: boolean;
  acdEnabled: boolean;
  directDialNumber: string | null;
  extension: string | null;
  twilioWorkerSid: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  softDeletedAt: string | null;
  // Computed server-side: PBX heuristic + live TaskRouter Activity name.
  presence: "online" | "offline";
  acdState: string | null;
};

type SortKey = "email" | "role" | "status" | "presence" | "lastLoginAt" | "createdAt";
type SortDir = "asc" | "desc";

const FACETABLE_FIELDS: Array<{ key: keyof User; label: string }> = [
  { key: "role", label: "Role" },
  { key: "status", label: "Status" },
  { key: "presence", label: "Presence" },
  { key: "pbxEnabled", label: "PBX" },
  { key: "acdEnabled", label: "ACD" },
  { key: "roleAssignmentSource", label: "Source" },
];

/**
 * Badge styling for live TaskRouter ACD activity names. Matched
 * case-insensitively on common activity vocab; unknown activities get the
 * neutral style rather than vanishing.
 */
function acdBadgeClass(activity: string): string {
  const a = activity.toLowerCase();
  if (a.includes("available")) return "bg-emerald-100 text-emerald-800";
  if (a.includes("busy") || a.includes("reserved")) return "bg-amber-100 text-amber-800";
  if (a.includes("wrap")) return "bg-blue-100 text-blue-800";
  if (a.includes("offline") || a.includes("unavailable")) return "bg-rogue-100 text-rogue-600";
  return "bg-rogue-50 text-rogue-700";
}

const STATUSES = ["active", "disabled", "pending"];

export function UsersView({ me }: { me: { id: string; role: AppRole } }) {
  const [items, setItems] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("email");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [facetFilters, setFacetFilters] = useState<Record<string, Set<string>>>({});
  const [editing, setEditing] = useState<Record<string, Partial<User>>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const canEdit = me.role === "Administrator";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api<{ items: User[] }>("/api/admin/users");
      setItems(res.items);
      setError(null);
    } catch (e) {
      if (e instanceof ApiError) setError(`${e.status}: ${(e.body as { error?: string })?.error ?? e.message}`);
      else setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Compute facet values (only render facet UI if <10 distinct values per the rule).
  const facets = useMemo(() => {
    const result: Record<string, Array<{ value: string; count: number }>> = {};
    for (const f of FACETABLE_FIELDS) {
      const counts = new Map<string, number>();
      for (const u of items) {
        const v = String(u[f.key] ?? "—");
        counts.set(v, (counts.get(v) ?? 0) + 1);
      }
      if (counts.size > 0 && counts.size < 10) {
        result[f.key] = [...counts.entries()]
          .map(([value, count]) => ({ value, count }))
          .sort((a, b) => (b.count - a.count) || a.value.localeCompare(b.value));
      }
    }
    return result;
  }, [items]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((u) => {
      if (term) {
        const hay = [u.email, u.displayName, u.directDialNumber].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(term)) return false;
      }
      for (const [key, allowed] of Object.entries(facetFilters)) {
        if (allowed.size === 0) continue;
        const v = String((u as Record<string, unknown>)[key] ?? "—");
        if (!allowed.has(v)) return false;
      }
      return true;
    });
  }, [items, search, facetFilters]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = (a as Record<string, unknown>)[sortKey];
      const bv = (b as Record<string, unknown>)[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const toggleFacet = (key: string, value: string) => {
    setFacetFilters((prev) => {
      const next = { ...prev };
      const set = new Set(next[key] ?? []);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      next[key] = set;
      return next;
    });
  };

  const startEdit = (u: User) => {
    setEditing({ ...editing, [u.id]: { ...u } });
  };
  const cancelEdit = (id: string) => {
    const next = { ...editing };
    delete next[id];
    setEditing(next);
  };
  const updateEditField = (id: string, field: keyof User, val: unknown) => {
    setEditing({ ...editing, [id]: { ...editing[id], [field]: val } });
  };

  const saveEdit = async (id: string) => {
    const draft = editing[id];
    if (!draft) return;
    setSavingId(id);
    try {
      // Validate phone if present
      if (draft.directDialNumber) {
        const p = parseUSPhone(draft.directDialNumber);
        if (!p.ok) {
          setError(`Invalid number: ${p.reason}`);
          setSavingId(null);
          return;
        }
        draft.directDialNumber = p.e164;
      }
      const body: Record<string, unknown> = {};
      const original = items.find((u) => u.id === id);
      if (!original) return;
      for (const k of ["role", "directDialNumber", "extension", "pbxEnabled", "acdEnabled", "status"] as const) {
        if ((draft as Record<string, unknown>)[k] !== (original as Record<string, unknown>)[k]) {
          body[k] = (draft as Record<string, unknown>)[k];
        }
      }
      if (Object.keys(body).length === 0) {
        cancelEdit(id);
        return;
      }
      await api(`/api/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(body) });
      await load();
      cancelEdit(id);
      setError(null);
    } catch (e) {
      const msg = e instanceof ApiError
        ? `${e.status}: ${(e.body as { error?: string; message?: string })?.message ?? (e.body as { error?: string })?.error ?? e.message}`
        : (e as Error).message;
      setError(msg);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="pad-fluid">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h1 className="text-3xl font-bold text-rogue-900">Users</h1>
          {!canEdit && (
            <div className="text-xs text-rogue-500 mt-1">
              Read-only — Administrator role required for edits.
            </div>
          )}
        </div>
        <button
          onClick={() => void load()}
          className="text-sm text-rogue-600 hover:text-rogue-800 flex items-center gap-1"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 rounded-lg pad-fluid mb-4 text-sm">{error}</div>
      )}

      {/* Search */}
      <div className="bg-white rounded-xl shadow pad-fluid mb-4">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-rogue-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email, name, or dial number"
            className="flex-1 outline-none text-sm"
          />
        </div>
      </div>

      {/* Facets */}
      {Object.keys(facets).length > 0 && (
        <div className="bg-white rounded-xl shadow pad-fluid mb-4 flex flex-wrap gap-fluid">
          {FACETABLE_FIELDS.filter((f) => facets[f.key]).map((f) => {
            const values = facets[f.key]!;
            const selected = facetFilters[f.key] ?? new Set();
            return (
              <div key={f.key as string} className="flex flex-col gap-1 min-w-[8rem]">
                <div className="text-xs uppercase tracking-wide text-rogue-500">{f.label}</div>
                <div className="flex flex-wrap gap-1">
                  {values.map((v) => {
                    const on = selected.has(v.value);
                    return (
                      <button
                        key={v.value}
                        onClick={() => toggleFacet(f.key as string, v.value)}
                        className={[
                          "text-xs px-2 py-1 rounded-full border transition-colors",
                          on ? "bg-rogue-700 text-white border-rogue-700" : "bg-rogue-50 text-rogue-700 border-rogue-200 hover:bg-rogue-100",
                        ].join(" ")}
                      >
                        {v.value} <span className="opacity-60">{v.count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-rogue-50">
            <tr className="text-left text-xs uppercase tracking-wide text-rogue-600">
              <Th onClick={() => toggleSort("email")} active={sortKey === "email"} dir={sortDir}>User</Th>
              <Th onClick={() => toggleSort("role")} active={sortKey === "role"} dir={sortDir}>Role</Th>
              <Th onClick={() => toggleSort("status")} active={sortKey === "status"} dir={sortDir}>Status</Th>
              <Th onClick={() => toggleSort("presence")} active={sortKey === "presence"} dir={sortDir}>Presence</Th>
              <th className="pad-fluid">PBX / ACD</th>
              <th className="pad-fluid">Ext.</th>
              <th className="pad-fluid">Direct dial</th>
              <Th onClick={() => toggleSort("lastLoginAt")} active={sortKey === "lastLoginAt"} dir={sortDir}>Last login</Th>
              <th className="pad-fluid w-[6rem]"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rogue-100">
            {sorted.length === 0 && !loading && (
              <tr><td colSpan={9} className="pad-fluid text-center text-rogue-500">No users match.</td></tr>
            )}
            {sorted.map((u) => {
              const draft = editing[u.id];
              const isEditing = !!draft;
              return (
                <tr key={u.id} className={isEditing ? "bg-amber-50" : ""}>
                  <td className="pad-fluid">
                    <div className="font-medium text-rogue-900">{u.displayName || u.email}</div>
                    <div className="text-xs text-rogue-500">{u.email}</div>
                  </td>
                  <td className="pad-fluid">
                    {isEditing ? (
                      <select
                        value={(draft.role as string) ?? u.role}
                        onChange={(e) => updateEditField(u.id, "role", e.target.value)}
                        className="text-sm border border-rogue-200 rounded px-2 py-1"
                      >
                        {ASSIGNABLE_ROLES.map((r) => (
                          <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="inline-flex items-center text-rogue-700">{ROLE_LABELS[u.role]}</span>
                    )}
                  </td>
                  <td className="pad-fluid">
                    {isEditing ? (
                      <select
                        value={(draft.status as string) ?? u.status}
                        onChange={(e) => updateEditField(u.id, "status", e.target.value)}
                        className="text-sm border border-rogue-200 rounded px-2 py-1"
                      >
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : (
                      <span className={[
                        "inline-flex text-xs px-2 py-1 rounded-full",
                        u.status === "active" ? "bg-emerald-100 text-emerald-800" :
                        u.status === "disabled" ? "bg-red-100 text-red-800" :
                        "bg-amber-100 text-amber-800",
                      ].join(" ")}>{u.status}</span>
                    )}
                  </td>
                  <td className="pad-fluid">
                    <div className="flex items-center gap-2">
                      {/* PBX presence dot — login-recency heuristic (15 min) */}
                      <span className="inline-flex items-center gap-1 text-xs text-rogue-700" title={`PBX ${u.presence}`}>
                        <span className={[
                          "w-2 h-2 rounded-full",
                          u.presence === "online" ? "bg-emerald-500" : "bg-rogue-300",
                        ].join(" ")} />
                        {u.presence}
                      </span>
                      {/* Live TaskRouter activity — only renders for ACD-wired users */}
                      {u.acdState && (
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${acdBadgeClass(u.acdState)}`}
                          title="ACD activity (TaskRouter)"
                        >
                          {u.acdState}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="pad-fluid">
                    {isEditing ? (
                      <div className="flex gap-2">
                        <label className="flex items-center gap-1 text-xs">
                          <input type="checkbox" checked={(draft.pbxEnabled as boolean | undefined) ?? u.pbxEnabled}
                            onChange={(e) => updateEditField(u.id, "pbxEnabled", e.target.checked)} />
                          PBX
                        </label>
                        <label className="flex items-center gap-1 text-xs">
                          <input type="checkbox" checked={(draft.acdEnabled as boolean | undefined) ?? u.acdEnabled}
                            onChange={(e) => updateEditField(u.id, "acdEnabled", e.target.checked)} />
                          ACD
                        </label>
                      </div>
                    ) : (
                      <div className="flex gap-1 text-xs">
                        {u.pbxEnabled && <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">PBX</span>}
                        {u.acdEnabled && <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">ACD</span>}
                      </div>
                    )}
                  </td>
                  <td className="pad-fluid">
                    {isEditing ? (
                      <input
                        value={(draft.extension as string | null) ?? ""}
                        onChange={(e) => updateEditField(u.id, "extension", e.target.value || null)}
                        placeholder="1000"
                        className="text-sm font-mono border border-rogue-200 rounded px-2 py-1 w-20"
                      />
                    ) : (
                      <span className="font-mono text-sm text-rogue-700">
                        {u.extension ? `x${u.extension}` : "—"}
                      </span>
                    )}
                  </td>
                  <td className="pad-fluid">
                    {isEditing ? (
                      <input
                        value={(draft.directDialNumber as string | null) ?? ""}
                        onChange={(e) => updateEditField(u.id, "directDialNumber", e.target.value || null)}
                        placeholder="+19185551234"
                        className="text-sm font-mono border border-rogue-200 rounded px-2 py-1 w-36"
                      />
                    ) : (
                      <span className="font-mono text-sm text-rogue-700">
                        {u.directDialNumber ? formatE164ForDisplay(u.directDialNumber) : "—"}
                      </span>
                    )}
                  </td>
                  <td className="pad-fluid text-xs text-rogue-500">
                    {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : "never"}
                  </td>
                  <td className="pad-fluid text-right">
                    {canEdit && (
                      isEditing ? (
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => void saveEdit(u.id)}
                            disabled={savingId === u.id}
                            className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white rounded px-2 py-1 text-xs flex items-center gap-1"
                          >
                            <Save className="w-3 h-3" /> Save
                          </button>
                          <button
                            onClick={() => cancelEdit(u.id)}
                            className="bg-rogue-100 hover:bg-rogue-200 text-rogue-700 rounded px-2 py-1 text-xs"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEdit(u)}
                          className="text-xs text-rogue-600 hover:text-rogue-900"
                        >
                          Edit
                        </button>
                      )
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children, onClick, active, dir }: {
  children: React.ReactNode;
  onClick: () => void;
  active: boolean;
  dir: SortDir;
}): JSX.Element {
  return (
    <th className="pad-fluid">
      <button onClick={onClick} className="flex items-center gap-1 hover:text-rogue-900">
        <span>{children}</span>
        {active && <Check className={`w-3 h-3 ${dir === "asc" ? "" : "rotate-180"}`} />}
      </button>
    </th>
  );
}
